import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, Reply, Heart, User, AtSign, Trash2, Flag, AlertTriangle, Check, Clock, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { postsService } from '../services/postsService';
import { reportsService } from '../services/reportsService';
import { moderationService } from '../services/moderationService';
import ReportDialog from './ReportDialog';
import { ContentModerationError } from '../lib/contentModeration';

interface Comment {
  id: string;
  content: string;
  author_id: string;
  parent_comment_id?: string;
  created_at: string;
  updated_at: string;
  likes_count: number;
  is_deleted?: boolean;
  deleted_at?: string;
  deleted_by?: string;
  moderation_status?: 'pending' | 'approved' | 'rejected';
  moderation_reason?: string | null;
  moderated_at?: string | null;
  moderated_by?: string | null;
  profiles: {
    username: string;
    full_name: string;
    avatar_url?: string;
  };
  replies?: Comment[];
}

interface CommentSectionProps {
  postId: string;
  comments: Comment[];
  onCommentsUpdate: (comments: Comment[]) => void;
}

interface UserSuggestion {
  id: string;
  username: string;
  full_name: string;
  avatar_url?: string;
}

const CommentSection: React.FC<CommentSectionProps> = ({ postId, comments, onCommentsUpdate }) => {
  const { user } = useAuth();
  const { select, locale, t } = useLanguage();
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<UserSuggestion[]>([]);
  const [mentionQuery, setMentionQuery] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [deletingComment, setDeletingComment] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const replyInputRef = useRef<HTMLInputElement>(null);
  const [likedCommentIds, setLikedCommentIds] = useState<Set<string>>(new Set());

  const isModerator = user?.user_type === 'moderator' || user?.user_type === 'admin';
  const currentUserId = user?.id || null;
  const [reportDialog, setReportDialog] = useState<{ open: boolean; comment: Comment | null }>({ open: false, comment: null });
  const [moderatingCommentId, setModeratingCommentId] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackVariant, setFeedbackVariant] = useState<'success' | 'error'>('success');

  // Process comments into nested structure
  const processComments = (commentsData: Comment[]): Comment[] => {
    const commentMap = new Map<string, Comment>();
    const rootComments: Comment[] = [];

    // First pass: create comment map
    commentsData.forEach(comment => {
      commentMap.set(comment.id, { ...comment, replies: [] });
    });

    // Second pass: organize into tree structure
    commentsData.forEach(comment => {
      if (comment.parent_comment_id) {
        const parent = commentMap.get(comment.parent_comment_id);
        if (parent) {
          parent.replies = parent.replies || [];
          parent.replies.push(commentMap.get(comment.id)!);
        }
      } else {
        rootComments.push(commentMap.get(comment.id)!);
      }
    });

    return rootComments;
  };

  const nestedComments = processComments(comments);

  // Fetch liked comments
  useEffect(() => {
    const fetchLikes = async () => {
      if (!user) return;
      const ids = comments.map(c => c.id);
      if (ids.length > 0) {
        try {
          const liked = await postsService.getLikedCommentIds(ids);
          setLikedCommentIds(new Set(liked));
        } catch (e) {
          console.error('Error fetching comment likes:', e);
        }
      }
    };
    fetchLikes();
  }, [comments, user]);

  const handleToggleLike = async (commentId: string) => {
    if (!user) return;
    try {
      const { liked } = await postsService.toggleCommentLike(commentId);

      setLikedCommentIds(prev => {
        const newSet = new Set(prev);
        if (liked) newSet.add(commentId);
        else newSet.delete(commentId);
        return newSet;
      });

      // Update count locally
      const updateLikeCount = (list: Comment[]): Comment[] => {
        return list.map(c => {
          if (c.id === commentId) {
            return { ...c, likes_count: (c.likes_count || 0) + (liked ? 1 : -1) };
          }
          // Shallow copy replies if they exist
          return { ...c };
        });
      };

      onCommentsUpdate(updateLikeCount(comments));

    } catch (err) {
      console.error('Error toggling like:', err);
    }
  };

  // CommentItem component moved outside to prevent re-renders
  const CommentItem: React.FC<{
    comment: Comment;
    level?: number;
    onReplyClick: (commentId: string, username: string) => void;
    onCancelReply: () => void;
    isReplying: boolean;
    onDeleteComment: (commentId: string) => void;
    onReportComment: (comment: Comment) => void;
    onModerateComment: (commentId: string, action: 'approve' | 'reject' | 'pending') => void;
    isModerator: boolean;
    currentUserId: string | null;
    moderatingCommentId: string | null;
    likedCommentIds: Set<string>;
    onToggleLike: (id: string) => void;
  }> = React.memo(({
    comment,
    level = 0,
    onReplyClick,
    onCancelReply,
    isReplying,
    onDeleteComment,
    onReportComment,
    onModerateComment,
    isModerator,
    currentUserId,
    moderatingCommentId,
    likedCommentIds,
    onToggleLike,
  }) => {
    const isAuthor = currentUserId === comment.author_id;
    const showModerationStatus = comment.moderation_status && comment.moderation_status !== 'approved';
    const isLiked = likedCommentIds.has(comment.id);
    const containerStatusClass = comment.moderation_status === 'pending'
      ? 'bg-yellow-50 border border-yellow-200'
      : comment.moderation_status === 'rejected'
        ? 'bg-red-50 border border-red-200'
        : 'bg-gray-100';

    return (
      <div className={`${level > 0 ? 'ml-6 border-l-2 border-gray-100 pl-4' : ''}`}>
        <div className="flex gap-3 mb-3">
          {comment.is_deleted ? (
            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
              <User className="text-gray-400" size={16} />
            </div>
          ) : (
            <img
              src={comment.profiles?.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=32&h=32&fit=crop'}
              alt={comment.profiles?.full_name}
              className="w-8 h-8 rounded-full object-cover cursor-pointer hover:scale-105 transition-transform"
              onClick={() => {
                const event = new CustomEvent('navigate', {
                  detail: { tab: 'profile', username: comment.profiles?.username }
                });
                window.dispatchEvent(event);
              }}
            />
          )}
          <div className="flex-1">
            {comment.is_deleted ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 break-words">
                <p className="font-medium text-sm text-gray-500 mb-1">
                  {select({ es: '[Usuario eliminado]', en: '[Deleted user]' })}
                </p>
                <div className="text-sm text-gray-400 italic">
                  {select({ es: 'Este comentario ya no está disponible', en: 'This comment is no longer available' })}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {select({ es: 'Eliminado el', en: 'Deleted on' })}{' '}
                  {comment.deleted_at
                    ? new Date(comment.deleted_at).toLocaleDateString(locale)
                    : select({ es: 'fecha desconocida', en: 'unknown date' })}
                </div>
              </div>
            ) : (
              <div className={`${containerStatusClass} rounded-lg px-3 py-2 break-words transition-colors`}>
                <div className="flex items-start justify-between mb-1">
                  <p
                    className="font-medium text-sm text-gray-900 cursor-pointer hover:text-purple-600 transition-colors"
                    onClick={() => {
                      const event = new CustomEvent('navigate', {
                        detail: { tab: 'profile', username: comment.profiles?.username }
                      });
                      window.dispatchEvent(event);
                    }}
                  >
                    {comment.profiles?.full_name}
                  </p>

                  {/* Delete button for own comments */}
                  {isAuthor && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteComment(comment.id);
                      }}
                      className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded hover:bg-gray-200"
                      title={select({ es: 'Eliminar comentario', en: 'Delete comment' })}
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
                {showModerationStatus && (
                  <div className={`inline-flex items-center gap-1 text-xs font-semibold mb-2 px-2 py-1 rounded-full ${comment.moderation_status === 'pending'
                    ? 'bg-yellow-200 text-yellow-800'
                    : 'bg-red-200 text-red-800'
                    }`}>
                    <AlertTriangle size={12} />
                    {comment.moderation_status === 'pending'
                      ? select({ es: 'Pendiente de moderación', en: 'Pending moderation' })
                      : select({ es: 'Comentario rechazado', en: 'Comment rejected' })}
                  </div>
                )}

                <div className="text-sm text-gray-700 break-words leading-relaxed">
                  {renderMentions(comment.content)}
                </div>

                {comment.moderation_reason && (isModerator || isAuthor) && (
                  <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded px-2 py-1">
                    <span className="font-semibold">
                      {select({ es: 'Motivo:', en: 'Reason:' })}
                    </span>{' '}
                    {comment.moderation_reason}
                  </div>
                )}
              </div>
            )}

            {/* Actions only for non-deleted comments */}
            {!comment.is_deleted && (
              <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-500">
                <button
                  onClick={() => onToggleLike(comment.id)}
                  className={`flex items-center gap-1 p-1 rounded hover:bg-gray-100 transition-colors ${isLiked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
                    }`}
                >
                  <Heart size={12} fill={isLiked ? 'currentColor' : 'none'} />
                  <span>{comment.likes_count || 0}</span>
                </button>
                <span>•</span>
                <span>{formatTimeAgo(comment.created_at)}</span>
                <span>•</span>
                <button
                  onClick={() => onReplyClick(comment.id, comment.profiles?.username || '')}
                  className="hover:text-purple-500 flex items-center gap-1 p-1 rounded hover:bg-gray-100 transition-colors"
                >
                  <Reply size={12} />
                  <span>{select({ es: 'Responder', en: 'Reply' })}</span>
                </button>

                {!isAuthor && !isModerator && (
                  <button
                    onClick={() => onReportComment(comment)}
                    className="hover:text-red-500 flex items-center gap-1 p-1 rounded hover:bg-gray-100 transition-colors"
                  >
                    <Flag size={12} />
                    <span>{select({ es: 'Reportar', en: 'Report' })}</span>
                  </button>
                )}
              </div>
            )}

            {isModerator && !comment.is_deleted && (
              <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
                <button
                  onClick={() => onModerateComment(comment.id, 'approve')}
                  disabled={moderatingCommentId === comment.id || comment.moderation_status === 'approved'}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60"
                >
                  <Check size={12} />
                  {select({ es: 'Aprobar', en: 'Approve' })}
                </button>
                <button
                  onClick={() => onModerateComment(comment.id, 'reject')}
                  disabled={moderatingCommentId === comment.id}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-60"
                >
                  <X size={12} />
                  {select({ es: 'Rechazar', en: 'Reject' })}
                </button>
                <button
                  onClick={() => onModerateComment(comment.id, 'pending')}
                  disabled={moderatingCommentId === comment.id || comment.moderation_status === 'pending'}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-60"
                >
                  <Clock size={12} />
                  {select({ es: 'Pendiente', en: 'Pending' })}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Reply form */}
        {isReplying && (
          <div className="ml-11 mb-4">
            <ReplyForm
              onSubmit={handleSubmitReply}
              onCancel={onCancelReply}
              replyContent={replyContent}
              submitting={submitting}
              showSuggestions={showSuggestions && replyTo === comment.id}
              suggestions={suggestions}
              onMentionSelect={(username) => insertMention(username, true)}
              onInputChange={(value) => handleInputChange(value, true)}
            />
          </div>
        )}

        {/* Nested replies */}
        {comment.replies && comment.replies.length > 0 && (
          <div className="space-y-3">
            {comment.replies.map((reply) => (
              <CommentItem
                key={reply.id}
                comment={reply}
                level={level + 1}
                onReplyClick={onReplyClick}
                onCancelReply={onCancelReply}
                isReplying={false} // Nested replies don't show reply form to keep UI clean
                onDeleteComment={onDeleteComment}
                onReportComment={onReportComment}
                onModerateComment={onModerateComment}
                isModerator={isModerator}
                currentUserId={currentUserId}
                moderatingCommentId={moderatingCommentId}
                likedCommentIds={likedCommentIds}
                onToggleLike={onToggleLike}
              />
            ))}
          </div>
        )}
      </div>
    );
  });

  // Separate ReplyForm component to prevent re-renders
  const ReplyForm: React.FC<{
    onSubmit: (e: React.FormEvent) => void;
    onCancel: () => void;
    replyContent: string;
    submitting: boolean;
    showSuggestions: boolean;
    suggestions: UserSuggestion[];
    onMentionSelect: (username: string) => void;
    onInputChange: (value: string) => void;
  }> = React.memo(({
    onSubmit,
    onCancel,
    replyContent,
    submitting,
    showSuggestions,
    suggestions,
    onMentionSelect,
    onInputChange
  }) => (
    <form onSubmit={onSubmit} className="relative">
      <div className="flex gap-2">
        <input
          ref={replyInputRef}
          type="text"
          value={replyContent}
          onChange={(e) => onInputChange(e.target.value)}
          placeholder={select({ es: 'Escribe tu respuesta... (usa @ para mencionar)', en: 'Write your reply... (use @ to mention)' })}
          className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          autoComplete="off"
          autoFocus
        />
        <button
          type="submit"
          disabled={submitting || !replyContent.trim()}
          className="px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
        >
          {submitting ? '...' : select({ es: 'Responder', en: 'Reply' })}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          {select({ es: 'Cancelar', en: 'Cancel' })}
        </button>
      </div>

      {/* User suggestions for replies */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-32 overflow-y-auto">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.id}
              type="button"
              onClick={() => onMentionSelect(suggestion.username)}
              className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <img
                src={suggestion.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=24&h=24&fit=crop'}
                alt={suggestion.full_name}
                className="w-5 h-5 rounded-full object-cover"
              />
              <div>
                <span className="font-medium text-gray-900">{suggestion.full_name}</span>
                <span className="text-gray-500 text-sm ml-2">@{suggestion.username}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </form>
  ));

  // Handlers for reply actions
  const handleReplyClick = (commentId: string, username: string) => {
    setReplyTo(commentId);
    setReplyContent(`@${username} `);
    setShowSuggestions(false);
    setSuggestions([]);
    setMentionQuery('');
    setTimeout(() => replyInputRef.current?.focus(), 100);
  };

  const handleCancelReply = () => {
    setReplyTo(null);
    setReplyContent('');
    setShowSuggestions(false);
    setSuggestions([]);
    setMentionQuery('');
  };

  const handleDeleteComment = async (commentId: string) => {
    setDeletingComment(commentId);
    try {
      const result = await postsService.deleteComment(commentId);

      if (result.type === 'soft_delete') {
        // Update local state to show deleted comment
        const updatedComments = comments.map(comment =>
          comment.id === commentId
            ? {
              ...comment,
              is_deleted: true,
              deleted_at: new Date().toISOString(),
              deleted_by: user?.id
            }
            : comment
        );
        onCommentsUpdate(updatedComments);
      } else {
        // Hard delete - remove from list
        const updatedComments = comments.filter(comment => comment.id !== commentId);
        onCommentsUpdate(updatedComments);
      }

      setShowDeleteModal(null);
    } catch (err) {
      console.error('Error deleting comment:', err);
      alert(
        select({ es: 'Error al eliminar comentario: ', en: 'Error deleting comment: ' }) +
        (err instanceof Error ? err.message : select({ es: 'Error desconocido', en: 'Unknown error' }))
      );
    } finally {
      setDeletingComment(null);
    }
  };

  // Handle mention detection
  const handleInputChange = (value: string, isReply: boolean = false) => {
    if (isReply) {
      setReplyContent(value);
    } else {
      setNewComment(value);
    }

    // Check if there's already a complete mention (@ followed by username and space)
    const completeMentionRegex = /@\w+\s/g;
    const completeMentions = value.match(completeMentionRegex);

    // If there's already a complete mention, don't search for more
    if (completeMentions && completeMentions.length > 0) {
      setShowSuggestions(false);
      setSuggestions([]);
      setMentionQuery('');
      return;
    }

    // Only detect mentions if there's an @ symbol followed by text (and no complete mentions)
    const atIndex = value.lastIndexOf('@');
    if (atIndex !== -1 && atIndex < value.length - 1) {
      const afterAt = value.substring(atIndex + 1);
      const spaceIndex = afterAt.indexOf(' ');
      const mentionText = spaceIndex === -1 ? afterAt : afterAt.substring(0, spaceIndex);

      // Only show suggestions if actively typing after @ and no spaces in the mention text
      if (mentionText.length > 0 && mentionText.length <= 20 && !mentionText.includes('\n') && !mentionText.includes(' ')) {
        setMentionQuery(mentionText);
        searchUsers(mentionText);
        setShowSuggestions(true);
        setCursorPosition(atIndex);
      } else {
        setShowSuggestions(false);
        setSuggestions([]);
        setMentionQuery('');
      }
    } else {
      setShowSuggestions(false);
      setSuggestions([]);
      setMentionQuery('');
    }
  };

  const searchUsers = async (query: string) => {
    // Only search if query has at least 1 character
    if (query.length < 1) {
      setSuggestions([]);
      return;
    }

    try {
      const users = await postsService.searchUsers(query);
      setSuggestions(users);
    } catch (err) {
      console.error('Error searching users:', err);
      setSuggestions([]);
    }
  };

  const insertMention = (username: string, isReply: boolean = false) => {
    const currentText = isReply ? replyContent : newComment;
    const beforeMention = currentText.substring(0, cursorPosition);
    const afterMention = currentText.substring(cursorPosition + mentionQuery.length + 1);
    const newText = `${beforeMention}@${username} ${afterMention}`;

    if (isReply) {
      setReplyContent(newText);
      // Focus back to reply input
      setTimeout(() => replyInputRef.current?.focus(), 100);
    } else {
      setNewComment(newText);
      // Focus back to main input
      setTimeout(() => inputRef.current?.focus(), 100);
    }

    setShowSuggestions(false);
    setMentionQuery('');
    setSuggestions([]);
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      const comment = await postsService.addComment(postId, newComment.trim());

      // Update comments list
      const updatedComments = [...comments, comment];
      onCommentsUpdate(updatedComments);

      setNewComment('');
      setShowSuggestions(false);
    } catch (err) {
      if (err instanceof ContentModerationError) {
        const key = err.categories.includes('sexual')
          ? 'moderation.block.sexual'
          : err.categories.includes('profanity')
            ? 'moderation.block.profanity'
            : 'moderation.block.generic';
        setFeedbackVariant('error');
        setFeedbackMessage(t(key, 'Your content violates our community guidelines.'));
        setTimeout(() => setFeedbackMessage(''), 5000);
      } else {
        console.error('Error adding comment:', err);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyContent.trim() || !replyTo) return;

    setSubmitting(true);
    try {
      const reply = await postsService.addComment(postId, replyContent.trim(), replyTo);

      // Update comments list
      const updatedComments = [...comments, reply];
      onCommentsUpdate(updatedComments);

      setReplyContent('');
      setReplyTo(null);
      setShowSuggestions(false);
    } catch (err) {
      if (err instanceof ContentModerationError) {
        const key = err.categories.includes('sexual')
          ? 'moderation.block.sexual'
          : err.categories.includes('profanity')
            ? 'moderation.block.profanity'
            : 'moderation.block.generic';
        setFeedbackVariant('error');
        setFeedbackMessage(t(key, 'Your content violates our community guidelines.'));
        setTimeout(() => setFeedbackMessage(''), 5000);
      } else {
        console.error('Error adding reply:', err);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleReportCommentStart = (comment: Comment) => {
    setReportDialog({ open: true, comment });
  };

  const handleReportCommentSubmit = async ({ reason, description }: { reason: string; description?: string }) => {
    if (!reportDialog.comment) return;

    try {
      await reportsService.createReport({
        itemType: 'comment',
        itemId: reportDialog.comment.id,
        reason,
        description,
      });
      setReportDialog({ open: false, comment: null });
      setFeedbackVariant('success');
      setFeedbackMessage(select({ es: 'Reporte enviado para revisión.', en: 'Report submitted for review.' }));
      setTimeout(() => setFeedbackMessage(''), 4000);
    } catch (err) {
      console.error('Error reporting comment:', err);
      setFeedbackVariant('error');
      setFeedbackMessage(err instanceof Error ? err.message : select({ es: 'No se pudo enviar el reporte.', en: 'Could not send the report.' }));
      setTimeout(() => setFeedbackMessage(''), 5000);
    }
  };

  const handleModerateComment = async (commentId: string, action: 'approve' | 'reject' | 'pending') => {
    try {
      let reason: string | undefined;

      if (action === 'reject') {
        const input = window.prompt(select({ es: 'Motivo del rechazo', en: 'Reason for rejection' }), '') || '';
        if (!input.trim()) {
          return;
        }
        reason = input.trim();
      }

      setModeratingCommentId(commentId);
      const updatedComment = await moderationService.moderateComment({
        commentId,
        action,
        reason,
      });

      const updatedComments = comments.map((comment) =>
        comment.id === commentId
          ? {
            ...comment,
            ...updatedComment,
            profiles: comment.profiles,
          }
          : comment
      );
      onCommentsUpdate(updatedComments);

      const message = action === 'approve'
        ? select({ es: 'Comentario aprobado.', en: 'Comment approved.' })
        : action === 'reject'
          ? select({ es: 'Comentario rechazado.', en: 'Comment rejected.' })
          : select({ es: 'Comentario marcado como pendiente.', en: 'Comment marked as pending.' });
      setFeedbackVariant('success');
      setFeedbackMessage(message);
      setTimeout(() => setFeedbackMessage(''), 4000);
    } catch (err) {
      console.error('Error moderating comment:', err);
      setFeedbackVariant('error');
      setFeedbackMessage(err instanceof Error ? err.message : select({ es: 'No se pudo actualizar el comentario.', en: 'Could not update the comment.' }));
      setTimeout(() => setFeedbackMessage(''), 5000);
    } finally {
      setModeratingCommentId(null);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return select({ es: 'Ahora', en: 'Just now' });
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
    return date.toLocaleDateString(locale);
  };

  const renderMentions = (text: string) => {
    const mentionRegex = /@(\w+)/g;
    const parts = text.split(mentionRegex);

    return parts.map((part, index) => {
      if (index % 2 === 1) {
        // This is a username (odd indices after split)
        return (
          <span
            key={index}
            className="text-purple-600 font-medium cursor-pointer hover:text-purple-700 hover:underline"
            onClick={() => {
              const event = new CustomEvent('navigate', {
                detail: { tab: 'profile', username: part }
              });
              window.dispatchEvent(event);
            }}
          >
            @{part}
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  return (
    <div className="space-y-4">
      <h4 className="font-medium text-gray-900 flex items-center gap-2">
        <MessageCircle size={16} />
        {select({ es: 'Comentarios', en: 'Comments' })} ({comments.length})
      </h4>

      {feedbackMessage && (
        <div
          className={`px-3 py-2 rounded-lg text-sm border ${feedbackVariant === 'error'
            ? 'bg-red-50 border-red-200 text-red-700'
            : 'bg-green-50 border-green-200 text-green-700'
            }`}
        >
          {feedbackMessage}
        </div>
      )}

      {/* Add Comment Form */}
      <form onSubmit={handleSubmitComment} className="relative">
        <div className="flex gap-3">
          <img
            src={user?.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=32&h=32&fit=crop'}
            alt={select({ es: 'Tu avatar', en: 'Your avatar' })}
            className="w-8 h-8 rounded-full object-cover"
          />
          <div className="flex-1 relative">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={newComment}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={select({ es: 'Añade un comentario... (opcional: usa @ para mencionar)', en: 'Add a comment... (optionally use @ to mention)' })}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                disabled={!user}
                autoComplete="off"
              />
              <button
                type="submit"
                disabled={submitting || !newComment.trim() || !user}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                {submitting ? '...' : select({ es: 'Enviar', en: 'Send' })}
              </button>
            </div>

            {/* User suggestions */}
            {showSuggestions && suggestions.length > 0 && !replyTo && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-40 overflow-y-auto">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion.id}
                    type="button"
                    onClick={() => insertMention(suggestion.username)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors flex items-center gap-3"
                  >
                    <img
                      src={suggestion.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=24&h=24&fit=crop'}
                      alt={suggestion.full_name}
                      className="w-6 h-6 rounded-full object-cover"
                    />
                    <div>
                      <div className="font-medium text-gray-900">{suggestion.full_name}</div>
                      <div className="text-gray-500 text-sm">@{suggestion.username}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {!user && (
              <p className="text-xs text-gray-500 mt-1">{select({ es: 'Inicia sesión para comentar', en: 'Sign in to comment' })}</p>
            )}
          </div>
        </div>
      </form>
      {/* Mention Help */}
      {(newComment.includes('@') || replyContent.includes('@')) && showSuggestions && (
        <div className="mt-2 text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded px-2 py-1">
          {select({ es: '💡 Escribiendo después de @ aparecerán sugerencias de usuarios', en: '💡 Typing after @ will show user suggestions' })}
        </div>
      )}

      {/* Comments List */}
      <div className="space-y-4">
        {nestedComments.map((comment) => (
          <CommentItem
            key={comment.id}
            comment={comment}
            onReplyClick={handleReplyClick}
            onCancelReply={handleCancelReply}
            isReplying={replyTo === comment.id}
            onDeleteComment={(id) => setShowDeleteModal(id)}
            onReportComment={handleReportCommentStart}
            onModerateComment={handleModerateComment}
            isModerator={isModerator}
            currentUserId={currentUserId}
            moderatingCommentId={moderatingCommentId}
            likedCommentIds={likedCommentIds}
            onToggleLike={handleToggleLike}
          />
        ))}

        {comments.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <MessageCircle size={32} className="mx-auto mb-2 text-gray-300" />
            <p className="text-sm">{select({ es: 'No hay comentarios aún', en: 'No comments yet' })}</p>
            <p className="text-sm">{select({ es: '¡Sé el primero en comentar!', en: 'Be the first to comment!' })}</p>
          </div>
        )}
      </div>

      {/* Mention Guide */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
        <div className="flex items-center gap-2 text-blue-700">
          <AtSign size={14} />
          <span className="font-medium">{select({ es: 'Consejos para comentarios:', en: 'Comment tips:' })}</span>
        </div>
        <ul className="text-blue-600 mt-2 space-y-1">
          <li>{select({ es: '• Usa <strong>@usuario</strong> para mencionar (máximo 1 por comentario)', en: '• Use <strong>@username</strong> to mention (max 1 per comment)' })}</li>
          <li>{select({ es: '• Haz clic en <strong>"Responder"</strong> para crear conversaciones', en: '• Click <strong>"Reply"</strong> to start threads' })}</li>
          <li>{select({ es: '• Las menciones envían notificación al usuario mencionado', en: '• Mentions notify the mentioned user' })}</li>
          <li>{select({ es: '• Los comentarios anidados mantienen el hilo de conversación', en: '• Nested comments keep the conversation in context' })}</li>
          <li>{select({ es: '• Puedes <strong>eliminar</strong> tus propios comentarios con el ícono 🗑️', en: '• You can <strong>delete</strong> your own comments using the 🗑️ icon' })}</li>
        </ul>
      </div>

      <ReportDialog
        isOpen={reportDialog.open}
        title={select({ es: 'Reportar comentario', en: 'Report comment' })}
        itemDescription={reportDialog.comment ? reportDialog.comment.content.slice(0, 80) : undefined}
        onClose={() => setReportDialog({ open: false, comment: null })}
        onSubmit={handleReportCommentSubmit}
      />

      {/* Delete Comment Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trash2 className="text-red-600" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{select({ es: '¿Eliminar comentario?', en: 'Delete comment?' })}</h3>

                {/* Check if comment has replies */}
                {(() => {
                  const hasReplies = comments.some(c => c.parent_comment_id === showDeleteModal);

                  return hasReplies ? (
                    <div className="space-y-3">
                      <p className="text-gray-600">
                        {select({ es: 'Este comentario tiene respuestas. Se ocultará pero las respuestas se mantendrán visibles.', en: 'This comment has replies. It will be hidden but the replies will remain visible.' })}
                      </p>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm text-blue-700">
                          {select({
                            es: '💬 <strong>Se preservará el hilo:</strong> Tu comentario mostrará "Este comentario ya no está disponible" pero las respuestas seguirán visibles para mantener la conversación.',
                            en: '💬 <strong>The thread will remain:</strong> Your comment will display "This comment is no longer available" but replies will stay visible to preserve the conversation.'
                          })}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-gray-600">
                        {select({ es: 'Este comentario se eliminará permanentemente.', en: 'This comment will be permanently deleted.' })}
                      </p>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-sm text-red-700">
                          {select({
                            es: '⚠️ <strong>Eliminación permanente:</strong> No tiene respuestas, por lo que se eliminará completamente.',
                            en: '⚠️ <strong>Permanent removal:</strong> It has no replies, so it will be fully removed.'
                          })}
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(null)}
                  disabled={deletingComment === showDeleteModal}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 font-medium"
                >
                  {select({ es: 'Cancelar', en: 'Cancel' })}
                </button>
                <button
                  onClick={() => handleDeleteComment(showDeleteModal)}
                  disabled={deletingComment === showDeleteModal}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 font-medium flex items-center justify-center gap-2"
                >
                  {deletingComment === showDeleteModal ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {select({ es: 'Eliminando...', en: 'Deleting...' })}
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      {select({ es: 'Eliminar comentario', en: 'Delete comment' })}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommentSection;
